import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { TbCopy, TbTrash } from "react-icons/tb";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import {
  listBaasEntityKycInvites,
  revokeBaasEntityKycInvite,
} from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceFeatures } from "../../../../../Services/baasInstancesApi";

const KYC_INVITE_FEATURE = "kyc.invite_link";

const STATUS_LABEL_KEYS = {
  pending: "baas.entidades.detail.verifications.inviteStatus.pending",
  contact_steps: "baas.entidades.detail.verifications.inviteStatus.contactSteps",
  veriff: "baas.entidades.detail.verifications.inviteStatus.veriff",
  completed: "baas.entidades.detail.verifications.inviteStatus.completed",
  expired: "baas.entidades.detail.verifications.inviteStatus.expired",
  revoked: "baas.entidades.detail.verifications.inviteStatus.revoked",
};

const statusBadgeStyles = {
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  contact_steps: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  veriff: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400",
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  expired: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  revoked: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

const resolveProxyErrorMessage = (error) => {
  const detail =
    error?.detail ||
    error?.response?.detail ||
    error?.body?.detail ||
    error?.data?.detail ||
    error?.message;
  if (detail && String(detail).trim()) return String(detail).trim();
  return intl.get("baas.entidades.detail.verifications.inviteError");
};

const formatInviteDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const buildInviteUrl = (pathOrToken) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = String(pathOrToken || "").startsWith("/")
    ? pathOrToken
    : `/auth/onboard/${pathOrToken}`;
  return `${origin}${path}`;
};

const mapInvite = (raw) => ({
  inviteId: raw?.invite_id ?? raw?.inviteId ?? null,
  token: raw?.token ?? "",
  status: String(raw?.status || "pending").toLowerCase(),
  path: raw?.path ?? (raw?.token ? `/auth/onboard/${raw.token}` : ""),
  kind: String(raw?.kind || "").toLowerCase(),
  updateScopes: Array.isArray(raw?.update_scopes)
    ? raw.update_scopes
    : Array.isArray(raw?.updateScopes)
      ? raw.updateScopes
      : [],
  includeLiveness: Boolean(raw?.include_liveness ?? raw?.includeLiveness ?? true),
  createdAt: raw?.created_at ?? raw?.createdAt ?? null,
  expiresAt: raw?.expires_at ?? raw?.expiresAt ?? null,
  completedAt: raw?.completed_at ?? raw?.completedAt ?? null,
  emailVerifiedAt: raw?.email_verified_at ?? raw?.emailVerifiedAt ?? null,
  phoneVerifiedAt: raw?.phone_verified_at ?? raw?.phoneVerifiedAt ?? null,
});

const describeInviteType = (invite) => {
  const parts = [];
  const scopes = Array.isArray(invite.updateScopes) ? invite.updateScopes : [];
  const scopeKeys = {
    personal: "baas.entidades.detail.verifications.generateScopePersonal",
    document: "baas.entidades.detail.verifications.generateScopeDocument",
    address: "baas.entidades.detail.verifications.generateScopeAddress",
  };
  scopes.forEach((scope) => {
    const key = scopeKeys[String(scope || "").toLowerCase()];
    if (key) parts.push(intl.get(key));
  });
  if (invite.includeLiveness) {
    parts.push(intl.get("baas.entidades.detail.verifications.generateScopeLiveness"));
  }
  if (invite.kind === "revalidation") {
    parts.unshift(intl.get("baas.entidades.detail.verifications.generateKindRevalidation"));
  }
  return parts.join(" · ");
};

/**
 * Entity KYC invite list (copy / revoke). Create lives in the generate modal.
 * Shown only when the user can create entities (`entities.create`).
 */
export const BaasEntidadeKycInvitesPanel = ({
  instance = null,
  entityId = null,
  isPersonEntity = true,
  refreshToken = 0,
}) => {
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const canCreate = can("entities.create");

  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const canShow =
    isPersonEntity && canCreate && !capabilitiesLoading && Boolean(instance && entityId);

  const loadFeatures = useCallback(async () => {
    if (!instance?.baasInstanceId || !canShow) {
      setFeatureEnabled(false);
      return;
    }
    try {
      const features = await fetchBaasInstanceFeatures(instance);
      const match = (Array.isArray(features) ? features : []).find(
        (item) =>
          String(item?.featureCode || "")
            .trim()
            .toLowerCase() === KYC_INVITE_FEATURE,
      );
      setFeatureEnabled(Boolean(match?.enabled));
    } catch {
      setFeatureEnabled(false);
    }
  }, [instance, canShow]);

  const loadInvites = useCallback(async () => {
    if (!instance || !entityId || !featureEnabled) return;
    setLoadingList(true);
    setError("");
    try {
      const rows = await listBaasEntityKycInvites(instance, entityId);
      setInvites(rows.map(mapInvite));
    } catch (err) {
      setError(resolveProxyErrorMessage(err));
    } finally {
      setLoadingList(false);
    }
  }, [instance, entityId, featureEnabled]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  useEffect(() => {
    if (featureEnabled) {
      loadInvites();
    }
  }, [featureEnabled, loadInvites, refreshToken]);

  if (!canShow || !featureEnabled) return null;

  const handleCopy = async (invite) => {
    const url = buildInviteUrl(invite.path || invite.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.inviteId);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleRevoke = async (invite) => {
    if (!invite?.inviteId || revokingId) return;
    setRevokingId(invite.inviteId);
    setError("");
    try {
      const raw = await revokeBaasEntityKycInvite(instance, entityId, invite.inviteId);
      const updated = mapInvite(raw);
      setInvites((prev) =>
        prev.map((row) => (row.inviteId === updated.inviteId ? updated : row)),
      );
    } catch (err) {
      setError(resolveProxyErrorMessage(err));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.inviteListTitle")}
        </h4>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.verifications.inviteListHint")}
        </p>
      </div>

      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {loadingList ? (
        <p className="text-xs text-gray-500">
          {intl.get("baas.entidades.detail.verifications.inviteLoading")}
        </p>
      ) : invites.length === 0 ? (
        <p className="text-xs text-gray-500">
          {intl.get("baas.entidades.detail.verifications.inviteEmpty")}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-brand-dark-border">
          {invites.map((invite) => {
            const statusKey = STATUS_LABEL_KEYS[invite.status] || STATUS_LABEL_KEYS.pending;
            const badge = statusBadgeStyles[invite.status] || statusBadgeStyles.pending;
            const canRevoke =
              invite.status !== "revoked" &&
              invite.status !== "completed" &&
              invite.status !== "expired";
            return (
              <li
                key={invite.inviteId || invite.token}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge}`}
                    >
                      {intl.get(statusKey) || invite.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {describeInviteType(invite)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {intl.get("baas.entidades.detail.verifications.inviteExpires", {
                      date: formatInviteDate(invite.expiresAt),
                    })}
                  </p>
                  <p className="font-mono text-[11px] text-gray-500 break-all">
                    {buildInviteUrl(invite.path || invite.token)}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopy(invite)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-brand-dark-border"
                  >
                    <TbCopy className="h-3.5 w-3.5" />
                    {copiedId === invite.inviteId
                      ? intl.get("baas.entidades.detail.verifications.copied")
                      : intl.get("baas.entidades.detail.verifications.copy")}
                  </button>
                  {canRevoke ? (
                    <button
                      type="button"
                      disabled={revokingId === invite.inviteId}
                      onClick={() => handleRevoke(invite)}
                      className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:text-red-400 disabled:opacity-50"
                    >
                      <TbTrash className="h-3.5 w-3.5" />
                      {intl.get("baas.entidades.detail.verifications.inviteRevoke")}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
